import { Flex, Text } from '@chakra-ui/react';
import { Button, Modal, TextArea, useToast } from '@morressier/alembic';
import React, { useEffect } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useCreateTagCategory, useUpdateTagCategory } from 'src/api/mutations';
import { FieldLabel } from 'src/components/Field';
import CustomColorPicker from 'src/components/FormField/CustomColorPicker';
import { useWorkspace } from '../../SubmissionContexts/Users/userHook';

export type ITagCategory = {
  color: string;
  name: string;
  description: string;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  isSubmitting?: boolean;
  data?: ITagCategory & {
    id?: string;
  };
};

const DEFAULT_VALUES: ITagCategory = {
  name: '',
  color: '',
  description: ''
};

const TAG_CATEGORY_NAME_MAX_LENGTH = 100;
const TAG_CATEGORY_DESCRIPTION_MAX_LENGTH = 100;

const validateRequiredText = (
  value: string,
  fieldLabel: string
): true | string => {
  if (!value?.length) {
    return `${fieldLabel} is required`;
  }

  if (!value.trim().length) {
    return `${fieldLabel} cannot contain only whitespace`;
  }

  if (value.length > TAG_CATEGORY_NAME_MAX_LENGTH) {
    return `Max ${TAG_CATEGORY_NAME_MAX_LENGTH} characters are allowed`;
  }

  return true;
};

const CreateTagCategory: React.FC<Props> = ({
  isOpen,
  onClose,
  isSubmitting = false,
  data
}) => {
  const methods = useForm<ITagCategory>({
    defaultValues: DEFAULT_VALUES
  });
  const { workspaceId } = useWorkspace();

  const toast = useToast();

  const createTagCategoryMutation = useCreateTagCategory(workspaceId);
  const updateTagCategoryMutation = useUpdateTagCategory(workspaceId);

  const {
    formState: { errors },
    handleSubmit,
    watch,
    register,
    reset
  } = methods;

  const isEditMode = Boolean(data?.id);
  const isMutationPending =
    createTagCategoryMutation.isPending || updateTagCategoryMutation.isPending;

  useEffect(() => {
    if (data) {
      reset(data);
    }
    if (!data || !isOpen) {
      reset(DEFAULT_VALUES);
    }
  }, [data, isOpen, reset]);

  const handleOnSubmit: SubmitHandler<ITagCategory> = async values => {
    const normalizedValues: ITagCategory = {
      ...values,
      name: values.name.trim(),
      description: values.description.trim()
    };

    const res = isEditMode
      ? await updateTagCategoryMutation.mutateAsync({
          categoryId: data?.id!,
          payload: normalizedValues
        })
      : await createTagCategoryMutation.mutateAsync(normalizedValues);

    if (res.isSuccess) {
      toast({
        variant: 'success',
        title: isEditMode ? 'Tag Category Updated' : 'New Tag Category Created'
      });

      onClose();
      reset(DEFAULT_VALUES);
      return;
    }

    return toast({
      variant: 'error',
      title:
        typeof res.message === 'string' ? res.message : 'Failed to create tag'
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {}}
      width="450px"
      closeButtonProps={{
        hidden: true
      }}
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(handleOnSubmit)}>
          <Flex flexDirection={'column'} gap={4} padding={6}>
            <Text textStyle="headingGroup">
              {isEditMode ? 'Edit Tag Category' : 'Create New Tag Category'}
            </Text>

            <Flex gap={2} direction={'column'} mt={3}>
              <Flex flex={1} gap={2} direction={'column'}>
                <FieldLabel label="Name" subText="" isRequired />
                <TextArea
                  placeholder="Enter category here"
                  value={watch('name')}
                  characterLimit={TAG_CATEGORY_NAME_MAX_LENGTH}
                  maxLength={TAG_CATEGORY_NAME_MAX_LENGTH}
                  isInvalid={!!errors.name}
                  errorMessage={errors.name?.message}
                  {...register('name', {
                    validate: value =>
                      validateRequiredText(value, 'Tag category')
                  })}
                  maxH={10}
                />
              </Flex>
              <Flex gap={2} direction={'column'}>
                <FieldLabel label="Description" subText="" isRequired />
                <TextArea
                  placeholder="Enter category description here"
                  value={watch('description')}
                  characterLimit={TAG_CATEGORY_DESCRIPTION_MAX_LENGTH}
                  maxLength={TAG_CATEGORY_DESCRIPTION_MAX_LENGTH}
                  isInvalid={!!errors.description}
                  errorMessage={errors.description?.message}
                  {...register('description', {
                    validate: value =>
                      validateRequiredText(value, 'Tag category description')
                  })}
                />
              </Flex>
              <Flex flex={1} gap={2} direction={'column'}>
                <FieldLabel label="Color" subText="" isRequired />
                <CustomColorPicker formField="color" />
              </Flex>
            </Flex>

            <Flex justifyContent="flex-end" gap={2} mt={3}>
              <Button variant="secondary" onClick={onClose} type="button">
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                isLoading={isSubmitting || isMutationPending}
              >
                {isEditMode ? 'Update Category' : 'Create Category'}
              </Button>
            </Flex>
          </Flex>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default CreateTagCategory;
